import {announce} from '@react-aria/live-announcer'
import {
  type CredentialResponse,
  googleLogout,
  GoogleOAuthProvider,
  useGoogleOneTapLogin,
} from '@react-oauth/google'
import {toast} from 'sonner'

import {GOOGLE_AUTH_CLIENT_ID} from '@/auth/config'
import {useIsAuthenticatedValue} from '@/auth/hooks/useIsAuthenticated'
import getAccountInformation from '@/auth/utils/getAccountInformation'
import getReferraId from '@/lib/referral/getReferralId'
import {signInWithGoogle} from '@/services/authenticationService'
import shouldLogError from '@/utils/api/shouldLogError'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'
import {logError} from '@/utils/logger'

const ERROR_MESSAGE =
  'Something went wrong while trying to login with Google. Please try again. If the problem persists, please contact support.'

function OneTap() {
  const router = useRouter()
  const referrerId = getReferraId()

  const handleOnSuccess = (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      toastErrorMessage(ERROR_MESSAGE)
      return
    }

    signInWithGoogle({
      token: credentialResponse.credential,
      referrerId,
    })
      .then(() => {
        // We have to use `getAccountInformation` because the account is not available in the store yet
        const account = getAccountInformation()

        if (!account) {
          throw new Error('Failed to get account information')
        }

        toast.success(`Logged in as ${account.accountName} (${account.email})`)
        announce(`Logged in as ${account.accountName} (${account.email})`)

        void router.invalidate()
      })
      .catch(e => {
        if (shouldLogError(e)) {
          logError(e)
        }
        toastErrorMessage(ERROR_MESSAGE)
        googleLogout()
      })
  }

  const handleOnError = () => {
    console.error('login failed')
  }

  useGoogleOneTapLogin({
    onSuccess: handleOnSuccess,
    onError: handleOnError,
    use_fedcm_for_prompt: true,
    cancel_on_tap_outside: false,
  })

  return null
}

export default memo(function GoogleOneTapLogin() {
  const isAuthenticated = useIsAuthenticatedValue()

  if (isAuthenticated) return

  return (
    <GoogleOAuthProvider clientId={GOOGLE_AUTH_CLIENT_ID}>
      <OneTap />
    </GoogleOAuthProvider>
  )
})
